<template>
  <div class="tiptap-editor">
    <!-- Menu Bar -->
    <v-toolbar dense flat color="grey lighten-4" class="tiptap-menu">
      <v-btn-toggle dense multiple>
        <!-- Text Formatting -->
        <v-btn
          x-small
          :class="{ 'v-btn--active': editor && editor.isActive('bold') }"
          @click="editor.chain().focus().toggleBold().run()"
          :title="$t('tiptap.bold')"
        >
          <v-icon small>mdi-format-bold</v-icon>
        </v-btn>

        <v-btn
          x-small
          :class="{ 'v-btn--active': editor && editor.isActive('italic') }"
          @click="editor.chain().focus().toggleItalic().run()"
          :title="$t('tiptap.italic')"
        >
          <v-icon small>mdi-format-italic</v-icon>
        </v-btn>

        <v-btn
          x-small
          :class="{ 'v-btn--active': editor && editor.isActive('underline') }"
          @click="editor.chain().focus().toggleUnderline().run()"
          :title="$t('tiptap.underline')"
        >
          <v-icon small>mdi-format-underline</v-icon>
        </v-btn>

        <v-btn
          x-small
          :class="{ 'v-btn--active': editor && editor.isActive('strike') }"
          @click="editor.chain().focus().toggleStrike().run()"
          :title="$t('tiptap.strike')"
        >
          <v-icon small>mdi-format-strikethrough</v-icon>
        </v-btn>
      </v-btn-toggle>

      <v-divider vertical class="mx-2"></v-divider>

      <!-- Headings -->
      <v-btn-toggle dense>
        <v-btn
          x-small
          :class="{ 'v-btn--active': editor && editor.isActive('heading', { level: 1 }) }"
          @click="editor.chain().focus().toggleHeading({ level: 1 }).run()"
          :title="$t('tiptap.heading1')"
        >
          H1
        </v-btn>

        <v-btn
          x-small
          :class="{ 'v-btn--active': editor && editor.isActive('heading', { level: 2 }) }"
          @click="editor.chain().focus().toggleHeading({ level: 2 }).run()"
          :title="$t('tiptap.heading2')"
        >
          H2
        </v-btn>

        <v-btn
          x-small
          :class="{ 'v-btn--active': editor && editor.isActive('heading', { level: 3 }) }"
          @click="editor.chain().focus().toggleHeading({ level: 3 }).run()"
          :title="$t('tiptap.heading3')"
        >
          H3
        </v-btn>
      </v-btn-toggle>

      <v-divider vertical class="mx-2"></v-divider>

      <!-- Lists -->
      <v-btn-toggle dense>
        <v-btn
          x-small
          :class="{ 'v-btn--active': editor && editor.isActive('bulletList') }"
          @click="editor.chain().focus().toggleBulletList().run()"
          :title="$t('tiptap.bullet_list')"
        >
          <v-icon small>mdi-format-list-bulleted</v-icon>
        </v-btn>

        <v-btn
          x-small
          :class="{ 'v-btn--active': editor && editor.isActive('orderedList') }"
          @click="editor.chain().focus().toggleOrderedList().run()"
          :title="$t('tiptap.ordered_list')"
        >
          <v-icon small>mdi-format-list-numbered</v-icon>
        </v-btn>
      </v-btn-toggle>

      <v-divider vertical class="mx-2"></v-divider>

      <!-- Alignment -->
      <v-btn-toggle dense>
        <v-btn
          x-small
          :class="{ 'v-btn--active': editor && editor.isActive({ textAlign: 'left' }) }"
          @click="editor.chain().focus().setTextAlign('left').run()"
          :title="$t('tiptap.align_left')"
        >
          <v-icon small>mdi-format-align-left</v-icon>
        </v-btn>

        <v-btn
          x-small
          :class="{ 'v-btn--active': editor && editor.isActive({ textAlign: 'center' }) }"
          @click="editor.chain().focus().setTextAlign('center').run()"
          :title="$t('tiptap.align_center')"
        >
          <v-icon small>mdi-format-align-center</v-icon>
        </v-btn>

        <v-btn
          x-small
          :class="{ 'v-btn--active': editor && editor.isActive({ textAlign: 'right' }) }"
          @click="editor.chain().focus().setTextAlign('right').run()"
          :title="$t('tiptap.align_right')"
        >
          <v-icon small>mdi-format-align-right</v-icon>
        </v-btn>

        <v-btn
          x-small
          :class="{ 'v-btn--active': editor && editor.isActive({ textAlign: 'justify' }) }"
          @click="editor.chain().focus().setTextAlign('justify').run()"
          :title="$t('tiptap.align_justify')"
        >
          <v-icon small>mdi-format-align-justify</v-icon>
        </v-btn>
      </v-btn-toggle>

      <v-divider vertical class="mx-2"></v-divider>

      <!-- Insert -->
      <v-btn-toggle dense>
        <v-menu offset-y>
          <template v-slot:activator="{ on }">
            <v-btn
              x-small
              v-on="on"
              :title="$t('tiptap.insert_image')"
            >
              <v-icon small>mdi-image</v-icon>
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item @click="openImageDialog">
              <v-list-item-icon>
                <v-icon small>mdi-upload</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ $t('tiptap.upload_image') }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item @click="openImageUrlDialog">
              <v-list-item-icon>
                <v-icon small>mdi-link</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ $t('tiptap.insert_by_url') }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-menu>
        
        <input
          ref="imageInput"
          type="file"
          accept="image/*"
          style="display: none"
          @change="handleImageUpload"
        />
      </v-btn-toggle>

      <!-- Image Resize Controls (shown when image is selected) -->
      <template v-if="editor && editor.isActive('image')">
        <v-divider vertical class="mx-2"></v-divider>
        <v-btn-toggle dense>
          <v-btn
            x-small
            @click="resizeImage('small')"
            :title="$t('tiptap.small_image')"
          >
            <v-icon x-small>mdi-image</v-icon>
            <span class="ml-1" style="font-size: 10px;">S</span>
          </v-btn>

          <v-btn
            x-small
            @click="resizeImage('medium')"
            :title="$t('tiptap.medium_image')"
          >
            <v-icon small>mdi-image</v-icon>
            <span class="ml-1" style="font-size: 10px;">M</span>
          </v-btn>

          <v-btn
            x-small
            @click="resizeImage('large')"
            :title="$t('tiptap.large_image')"
          >
            <v-icon small>mdi-image</v-icon>
            <span class="ml-1" style="font-size: 10px;">L</span>
          </v-btn>

          <v-btn
            x-small
            @click="resizeImage('full')"
            :title="$t('tiptap.full_image')"
          >
            <v-icon small>mdi-image</v-icon>
            <span class="ml-1" style="font-size: 10px;">XL</span>
          </v-btn>
        </v-btn-toggle>
      </template>

      <!-- Table Controls (shown when cursor is in table) -->
      <template v-if="editor && editor.isActive('table')">
        <v-divider vertical class="mx-2"></v-divider>
        <v-btn-toggle dense>
          <v-btn
            x-small
            @click="editor.chain().focus().addColumnBefore().run()"
            :title="$t('tiptap.add_column_before')"
          >
            <v-icon small>mdi-table-column-plus-before</v-icon>
          </v-btn>

          <v-btn
            x-small
            @click="editor.chain().focus().addColumnAfter().run()"
            :title="$t('tiptap.add_column_after')"
          >
            <v-icon small>mdi-table-column-plus-after</v-icon>
          </v-btn>

          <v-btn
            x-small
            @click="editor.chain().focus().deleteColumn().run()"
            :title="$t('tiptap.delete_column')"
            color="error"
          >
            <v-icon small>mdi-table-column-remove</v-icon>
          </v-btn>
        </v-btn-toggle>

        <v-btn-toggle dense class="ml-1">
          <v-btn
            x-small
            @click="editor.chain().focus().addRowBefore().run()"
            :title="$t('tiptap.add_row_before')"
          >
            <v-icon small>mdi-table-row-plus-before</v-icon>
          </v-btn>

          <v-btn
            x-small
            @click="editor.chain().focus().addRowAfter().run()"
            :title="$t('tiptap.add_row_after')"
          >
            <v-icon small>mdi-table-row-plus-after</v-icon>
          </v-btn>

          <v-btn
            x-small
            @click="editor.chain().focus().deleteRow().run()"
            :title="$t('tiptap.delete_row')"
            color="error"
          >
            <v-icon small>mdi-table-row-remove</v-icon>
          </v-btn>
        </v-btn-toggle>

        <v-btn-toggle dense class="ml-1">
          <v-btn
            x-small
            @click="editor.chain().focus().toggleHeaderRow().run()"
            :title="$t('tiptap.toggle_header_row')"
          >
            <v-icon small>mdi-table-headers-eye</v-icon>
          </v-btn>

          <v-btn
            x-small
            @click="editor.chain().focus().mergeCells().run()"
            :title="$t('tiptap.merge_cells')"
            :disabled="!editor.can().mergeCells()"
          >
            <v-icon small>mdi-table-merge-cells</v-icon>
          </v-btn>

          <v-btn
            x-small
            @click="editor.chain().focus().splitCell().run()"
            :title="$t('tiptap.split_cell')"
            :disabled="!editor.can().splitCell()"
          >
            <v-icon small>mdi-table-split-cell</v-icon>
          </v-btn>

          <v-btn
            x-small
            @click="editor.chain().focus().deleteTable().run()"
            :title="$t('tiptap.delete_table')"
            color="error"
          >
            <v-icon small>mdi-table-remove</v-icon>
          </v-btn>
        </v-btn-toggle>
      </template>

      <v-divider vertical class="mx-2"></v-divider>

      <v-btn-toggle dense>
        <v-btn
          x-small
          @click="addLink"
          :title="$t('tiptap.insert_link')"
        >
          <v-icon small>mdi-link</v-icon>
        </v-btn>

        <v-btn
          x-small
          @click="insertTable"
          :title="$t('tiptap.insert_table')"
        >
          <v-icon small>mdi-table</v-icon>
        </v-btn>
      </v-btn-toggle>

      <v-divider vertical class="mx-2"></v-divider>

      <!-- Text Color -->
      <v-menu offset-y>
        <template v-slot:activator="{ on }">
          <v-btn x-small v-on="on" :title="$t('tiptap.text_color')">
            <v-icon small>mdi-format-color-text</v-icon>
          </v-btn>
        </template>
        <v-card>
          <v-card-text>
            <v-color-picker
              v-model="textColor"
              @input="setTextColor"
              hide-inputs
              show-swatches
              swatches-max-height="200"
            ></v-color-picker>
          </v-card-text>
        </v-card>
      </v-menu>

      <v-menu offset-y>
        <template v-slot:activator="{ on }">
          <v-btn x-small v-on="on" :title="$t('tiptap.highlight_color')">
            <v-icon small>mdi-marker</v-icon>
          </v-btn>
        </template>
        <v-card>
          <v-card-text>
            <v-color-picker
              v-model="highlightColor"
              @input="setHighlightColor"
              hide-inputs
              show-swatches
              swatches-max-height="200"
            ></v-color-picker>
          </v-card-text>
        </v-card>
      </v-menu>

      <v-divider vertical class="mx-2"></v-divider>

      <!-- Other -->
      <v-btn-toggle dense>
        <v-btn
          x-small
          :class="{ 'v-btn--active': editor && editor.isActive('blockquote') }"
          @click="editor.chain().focus().toggleBlockquote().run()"
          :title="$t('tiptap.quote')"
        >
          <v-icon small>mdi-format-quote-close</v-icon>
        </v-btn>

        <v-btn
          x-small
          :class="{ 'v-btn--active': editor && editor.isActive('codeBlock') }"
          @click="editor.chain().focus().toggleCodeBlock().run()"
          :title="$t('tiptap.code_block')"
        >
          <v-icon small>mdi-code-tags</v-icon>
        </v-btn>

        <v-btn
          x-small
          @click="editor.chain().focus().setHorizontalRule().run()"
          :title="$t('tiptap.horizontal_rule')"
        >
          <v-icon small>mdi-minus</v-icon>
        </v-btn>
      </v-btn-toggle>
    </v-toolbar>

    <!-- Editor Content -->
    <div class="tiptap-editor-content">
      <editor-content v-if="editor && !isDestroying" :editor="editor" />
    </div>

    <!-- Table Size Dialog -->
    <v-dialog v-model="tableDialog" max-width="400">
      <v-card>
        <v-card-title>{{ $t('tiptap.insert_table') }}</v-card-title>
        <v-card-text>
          <v-text-field
            v-model.number="tableRows"
            type="number"
            :label="$t('tiptap.rows')"
            min="1"
            max="20"
            outlined
            dense
          ></v-text-field>
          <v-text-field
            v-model.number="tableCols"
            type="number"
            :label="$t('tiptap.columns')"
            min="1"
            max="10"
            outlined
            dense
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="tableDialog = false">{{ $t('custom.cancel') }}</v-btn>
          <v-btn color="primary" @click="confirmInsertTable">{{ $t('custom.insert') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Link Dialog -->
    <v-dialog v-model="linkDialog" max-width="500">
      <v-card>
        <v-card-title>{{ $t('tiptap.insert_link') }}</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="linkUrl"
            :label="$t('tiptap.url')"
            :placeholder="$t('tiptap.url_placeholder')"
            outlined
            dense
            autofocus
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-btn text color="error" @click="removeLink" v-if="linkUrl">
            {{ $t('tiptap.remove') }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn text @click="linkDialog = false">{{ $t('custom.cancel') }}</v-btn>
          <v-btn color="primary" @click="confirmInsertLink">{{ $t('custom.insert') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Image URL Dialog -->
    <v-dialog v-model="imageUrlDialog" max-width="500">
      <v-card>
        <v-card-title>{{ $t('tiptap.insert_image_url') }}</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="imageUrl"
            :label="$t('tiptap.image_url')"
            :placeholder="$t('tiptap.image_url_placeholder')"
            outlined
            dense
            autofocus
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="imageUrlDialog = false">{{ $t('custom.cancel') }}</v-btn>
          <v-btn color="primary" @click="confirmInsertImageUrl">{{ $t('custom.insert') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-2'
import StarterKit from '@tiptap/starter-kit'
import { TextAlign } from '@tiptap/extension-text-align'
import { Table } from '@tiptap/extension-table'
import { TableRow } from '@tiptap/extension-table-row'
import { TableCell } from '@tiptap/extension-table-cell'
import { TableHeader } from '@tiptap/extension-table-header'
import Image from '@tiptap/extension-image'
import { Link } from '@tiptap/extension-link'
import { TextStyle } from '@tiptap/extension-text-style'
import { Color } from '@tiptap/extension-color'
import { Highlight } from '@tiptap/extension-highlight'
import { Underline } from '@tiptap/extension-underline'

// Расширенное Image с поддержкой width и height
const CustomImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        renderHTML: attributes => {
          if (!attributes.width) {
            return {}
          }
          return {
            width: attributes.width,
          }
        },
      },
      height: {
        default: null,
        renderHTML: attributes => {
          if (!attributes.height) {
            return {}
          }
          return {
            height: attributes.height,
          }
        },
      },
    }
  },
})

export default {
  name: 'TiptapEditor',

  components: {
    EditorContent,
  },

  props: {
    value: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      editor: null,
      isDestroying: false,
      textColor: '#000000',
      highlightColor: '#ffff00',
      tableDialog: false,
      tableRows: 3,
      tableCols: 3,
      linkDialog: false,
      linkUrl: '',
      imageUrlDialog: false,
      imageUrl: '',
    }
  },

  computed: {
    isDark() {
      return this.$vuetify.theme.dark
    },
  },

  watch: {
    value(newValue) {
      // Prevent unnecessary updates
      if (!this.editor || this.editor.isDestroyed) {
        return
      }
      const isSame = this.editor.getHTML() === newValue
      if (isSame) {
        return
      }
      this.editor.commands.setContent(newValue, false)
    },
  },

  mounted() {
    this.editor = new Editor({
      content: this.value,
      extensions: [
        StarterKit,
        Underline,
        TextAlign.configure({
          types: ['heading', 'paragraph'],
        }),
        Table.configure({
          resizable: true,
        }),
        TableRow,
        TableHeader,
        TableCell,
        CustomImage.configure({
          inline: true,
          allowBase64: true,
          HTMLAttributes: {
            class: 'tiptap-image',
          },
        }),
        Link.configure({
          openOnClick: false,
        }),
        TextStyle,
        Color,
        Highlight.configure({
          multicolor: true,
        }),
      ],
      onUpdate: () => {
        // Emit HTML content
        this.$emit('input', this.editor.getHTML())
      },
    })
  },

  beforeDestroy() {
    // Устанавливаем флаг, чтобы скрыть EditorContent до уничтожения редактора
    this.isDestroying = true
  },

  destroyed() {
    // Уничтожаем редактор после того, как EditorContent скрыт
    if (!this.editor) {
      return
    }
    
    try {
      // Проверяем, что редактор еще не уничтожен
      if (!this.editor.isDestroyed) {
        this.editor.destroy()
      }
    } catch (error) {
      // Игнорируем ошибку, редактор уже уничтожен или не был полностью инициализирован
      console.debug('Editor cleanup skipped:', error.message)
    }
  },

  methods: {
    openImageDialog() {
      this.$refs.imageInput.click()
    },

    async handleImageUpload(event) {
      const file = event.target.files[0]
      if (!file) return

      // Проверка типа файла
      if (!file.type.startsWith('image/')) {
        this.$notify(
          this.$t('errors.error') || 'Error',
          this.$t('tiptap.image_file_type_error'),
          'error',
          3000
        )
        return
      }

      // Проверка размера файла (макс 5MB)
      if (file.size > 5 * 1024 * 1024) {
        this.$notify(
          this.$t('errors.error') || 'Error',
          this.$t('tiptap.image_size_error'),
          'error',
          3000
        )
        return
      }

      try {
        // Создаем FormData для загрузки
        const formData = new FormData()
        formData.append('file[]', file)  // Контроллер ожидает массив файлов
        formData.append('user_id', this.$store.getters.getCurrentUser.id)

        // FT-018: API base через store getter
        const endpoint = `${this.$store.getters.getApiUrl}/email_templates_image_upload`

        const response = await this.axios.post(endpoint, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': this.$store.getters.getAuthToken
          }
        })

        // Вставляем изображение в редактор
        if (response.data && response.data.file && response.data.file.url) {
          if (this.editor && !this.editor.isDestroyed) {
            this.editor.chain().focus().setImage({ src: response.data.file.url }).run()
          }
        }
      } catch (error) {
        console.error('Error uploading image:', error)
        this.$notify(
          this.$t('errors.error') || 'Error',
          this.$t('tiptap.image_upload_error'),
          'error',
          3000
        )
      }

      // Очищаем input для возможности повторной загрузки того же файла
      event.target.value = ''
    },

    openImageUrlDialog() {
      this.imageUrl = ''
      this.imageUrlDialog = true
    },

    confirmInsertImageUrl() {
      if (this.imageUrl && this.editor && !this.editor.isDestroyed) {
        this.editor.chain().focus().setImage({ src: this.imageUrl }).run()
      }
      this.imageUrlDialog = false
    },

    addLink() {
      if (!this.editor || this.editor.isDestroyed) {
        return
      }
      const previousUrl = this.editor.getAttributes('link').href
      this.linkUrl = previousUrl || ''
      this.linkDialog = true
    },

    confirmInsertLink() {
      if (this.linkUrl && this.editor && !this.editor.isDestroyed) {
        this.editor.chain().focus().extendMarkRange('link').setLink({ href: this.linkUrl }).run()
      }
      this.linkDialog = false
    },

    removeLink() {
      if (this.editor && !this.editor.isDestroyed) {
        this.editor.chain().focus().extendMarkRange('link').unsetLink().run()
      }
      this.linkDialog = false
    },

    insertTable() {
      this.tableDialog = true
    },

    confirmInsertTable() {
      if (this.tableRows > 0 && this.tableCols > 0 && this.tableRows <= 20 && this.tableCols <= 10) {
        if (this.editor && !this.editor.isDestroyed) {
          this.editor.chain().focus().insertTable({ 
            rows: this.tableRows, 
            cols: this.tableCols, 
            withHeaderRow: true 
          }).run()
        }
        this.tableDialog = false
      } else {
        this.$notify(
          this.$t('errors.error') || 'Error',
          this.$t('tiptap.table_validation_error'),
          'error',
          3000
        )
      }
    },

    setTextColor(color) {
      if (this.editor && !this.editor.isDestroyed) {
        this.editor.chain().focus().setColor(color).run()
      }
    },

    setHighlightColor(color) {
      if (this.editor && !this.editor.isDestroyed) {
        this.editor.chain().focus().setHighlight({ color }).run()
      }
    },

    resizeImage(size) {
      if (!this.editor || this.editor.isDestroyed) {
        return
      }
      
      const sizes = {
        small: '25%',
        medium: '50%',
        large: '75%',
        full: '100%'
      }
      
      this.editor.chain().focus().updateAttributes('image', { width: sizes[size] }).run()
    },
  },
}
</script>

<style scoped>
.tiptap-editor {
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;
}

.theme--dark .tiptap-editor {
  border-color: #424242;
}

.tiptap-menu {
  border-bottom: 1px solid #ddd;
  flex-wrap: wrap;
}

.theme--dark .tiptap-menu {
  border-bottom-color: #424242;
  background-color: #424242 !important;
}

.tiptap-editor-content {
  padding: 12px;
  min-height: 200px;
  max-height: 500px;
  overflow-y: auto;
  background-color: #fff;
}

.theme--dark .tiptap-editor-content {
  background-color: #1e1e1e;
}

/* Tiptap Editor Styles */
.tiptap-editor-content >>> .ProseMirror {
  min-height: 200px;
  outline: none;
  color: #000;
}

.theme--dark .tiptap-editor-content >>> .ProseMirror {
  color: #e0e0e0;
}

.tiptap-editor-content >>> .ProseMirror p {
  margin-bottom: 1em;
}

.tiptap-editor-content >>> .ProseMirror h1,
.tiptap-editor-content >>> .ProseMirror h2,
.tiptap-editor-content >>> .ProseMirror h3 {
  margin-top: 1em;
  margin-bottom: 0.5em;
  font-weight: bold;
}

.tiptap-editor-content >>> .ProseMirror h1 {
  font-size: 2em;
}

.tiptap-editor-content >>> .ProseMirror h2 {
  font-size: 1.5em;
}

.tiptap-editor-content >>> .ProseMirror h3 {
  font-size: 1.17em;
}

.tiptap-editor-content >>> .ProseMirror ul,
.tiptap-editor-content >>> .ProseMirror ol {
  padding-left: 1.5em;
  margin-bottom: 1em;
}

.tiptap-editor-content >>> .ProseMirror blockquote {
  border-left: 3px solid #ccc;
  padding-left: 1em;
  margin-left: 0;
  font-style: italic;
}

.theme--dark .tiptap-editor-content >>> .ProseMirror blockquote {
  border-left-color: #616161;
}

.tiptap-editor-content >>> .ProseMirror code {
  background-color: #f5f5f5;
  padding: 0.2em 0.4em;
  border-radius: 3px;
  font-family: monospace;
  color: #c7254e;
}

.theme--dark .tiptap-editor-content >>> .ProseMirror code {
  background-color: #2d2d2d;
  color: #f92672;
}

.tiptap-editor-content >>> .ProseMirror pre {
  background-color: #f5f5f5;
  padding: 1em;
  border-radius: 4px;
  overflow-x: auto;
}

.theme--dark .tiptap-editor-content >>> .ProseMirror pre {
  background-color: #2d2d2d;
}

.tiptap-editor-content >>> .ProseMirror pre code {
  background-color: transparent;
  padding: 0;
  color: inherit;
}

.tiptap-editor-content >>> .ProseMirror img {
  max-width: 100%;
  height: auto;
  cursor: pointer;
  display: inline-block;
}

.tiptap-editor-content >>> .ProseMirror img.ProseMirror-selectednode {
  outline: 3px solid #1976d2;
  position: relative;
}

/* Wrapper for resizable image */
.tiptap-editor-content >>> .ProseMirror .image-resizer {
  position: relative;
  display: inline-block;
  max-width: 100%;
}

.tiptap-editor-content >>> .ProseMirror .image-resizer img {
  display: block;
  width: 100%;
  height: auto;
}

.tiptap-editor-content >>> .ProseMirror table {
  border-collapse: collapse;
  table-layout: fixed;
  width: 100%;
  margin: 1em 0;
  overflow: hidden;
}

.tiptap-editor-content >>> .ProseMirror td,
.tiptap-editor-content >>> .ProseMirror th {
  min-width: 1em;
  border: 1px solid #ddd;
  padding: 8px;
  vertical-align: top;
  box-sizing: border-box;
  position: relative;
}

.theme--dark .tiptap-editor-content >>> .ProseMirror td,
.theme--dark .tiptap-editor-content >>> .ProseMirror th {
  border-color: #424242;
}

.tiptap-editor-content >>> .ProseMirror th {
  font-weight: bold;
  text-align: left;
  background-color: #f5f5f5;
}

.theme--dark .tiptap-editor-content >>> .ProseMirror th {
  background-color: #424242;
}

.tiptap-editor-content >>> .ProseMirror a {
  color: #1976d2;
  text-decoration: underline;
}

.tiptap-editor-content >>> .ProseMirror hr {
  border: none;
  border-top: 2px solid #ddd;
  margin: 1em 0;
}

.theme--dark .tiptap-editor-content >>> .ProseMirror hr {
  border-top-color: #424242;
}
</style>

